import React, { useState, useEffect } from 'react';
import { X, Share, Plus, Download } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Don't show if already installed
    if (standalone) return;

    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedDate = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedDate ? 
      (new Date() - dismissedDate) / (1000 * 60 * 60 * 24) : null;

    // Show again after 7 days
    if (dismissedDate && daysSinceDismissed < 7) return;

    // For Android/Desktop - listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS - show after a delay
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 max-w-md mx-auto">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <img 
              src="/icons/icon-192x192.png" 
              alt="Clubwiese" 
              className="w-10 h-10 rounded-lg"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">Clubwiese installieren</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Installieren Sie die App für schnelleren Zugriff
            </p>
          </div>
        </div>

        {isIOS ? (
          <div className="mt-4 bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-700 mb-2">
              <strong>So installieren Sie auf iPhone/iPad:</strong>
            </p>
            <ol className="text-sm text-gray-600 space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-medium">1</span>
                Tippen Sie auf <Share className="w-4 h-4 inline text-blue-500" /> (Teilen)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-medium">2</span>
                Scrollen Sie und wählen Sie <Plus className="w-4 h-4 inline" /> "Zum Home-Bildschirm"
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-medium">3</span>
                Tippen Sie auf "Hinzufügen"
              </li>
            </ol>
          </div>
        ) : (
          <div className="mt-4 flex gap-2">
            <Button 
              onClick={handleInstallClick}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Installieren
            </Button>
            <Button 
              variant="outline" 
              onClick={handleDismiss}
            >
              Später
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
